import React, { useState } from "react";
import { ellipsisHorizontal, checkmark, trash } from "ionicons/icons";
import { useCustomerAddressesStorageData } from "../../../../../providers/customerAddressesStorage";
import { Address } from "../../../../../core/types/entities";
import { getFullAddressForAddressType } from "../../../../../utils";
import classNames from "classnames/bind";
import styles from "../AddressForm.module.scss";
import { useDeleteCustomerAddress, useGetCustomerAddresses } from "../../../../../providers/CustomerAddresses";
import { useAuth } from "../../../../../contexts/auth-context";
import { useGetShippingZonesQuery } from "../../../../../providers/shippingAndPayment";
import { toast } from "react-toastify";
import { useConfig } from "../../../../../providers/config";
import { IonRadioGroup } from "@ionic/react";
import { IonIcon } from "@ionic/react";
import { useTranslation } from "react-i18next";
import useExpressDeliveryCheck from "../../../../../providers/address/useExpressDeliveryCheck";
import { shouldUseStaticOrderData } from "../../../../../utils/shouldUseStaticOrderData";

type AddressRadioGroupProps = {
  displayAddresses: Address[];
  openEditMode: (defaultValues: Address) => void;
};
const cx = classNames.bind(styles);

const AddressRadioGroup = ({ displayAddresses, openEditMode }: AddressRadioGroupProps) => {
  const { t } = useTranslation();
  const { currentShippingAddressId, setCurrentShippingAddressId, removeAddress } = useCustomerAddressesStorageData();
  const { user, isUserAuthenticated } = useAuth();
  const config = useConfig();
  const { refetch: refetchCustomerAddresses } = useGetCustomerAddresses({ customerId: user?.id! });
  const { mutateAsync: deleteCustomerAddressAsync } = useDeleteCustomerAddress();
  const { checkAndSetExpressMode } = useExpressDeliveryCheck();

  const {
    data: fetchedShippingZones,
    isLoading: isShippingZonesLoading,
    isError: isShippingZonesError,
  } = useGetShippingZonesQuery({
    onError: (err: string) => {
      toast.error(t("unexpectedErrorShippingZones"));
    },
    enabled: config?.shipping.isShippingZonesEnabled && config?.shipping.showDefaultShippingZone,
  });

  const shouldUseStaticShippingZones = shouldUseStaticOrderData({
    staticOrderDataEnabled: config.staticOrderDataEnabled,
    staticOrderData: config.staticOrderData,
    isLoading: isShippingZonesLoading,
    isError: isShippingZonesError,
  });

  const shippingZones = shouldUseStaticShippingZones ? config.staticOrderData?.shippingZones : fetchedShippingZones;

  const handleChangeSelectAddress = async (addressId: string) => {
    if (addressId) {
      setCurrentShippingAddressId(addressId, "CurrentShippingAddressSelect");
      const selectedAddress = displayAddresses.find((address) => address.id === addressId);
      if (config.isExpressDeliveryEnabled && selectedAddress?.latitude && selectedAddress?.longitude) {
        await checkAndSetExpressMode({
          latitude: selectedAddress?.latitude || "",
          longitude: selectedAddress?.longitude || "",
        });
      }
    }
  };
  const [deletingAddressId, setDeletingAddressId] = useState<string | null>(null);

  const handleDeleteAddress = async (addressId: string) => {
    setDeletingAddressId(addressId);

    try {
      if (isUserAuthenticated) {
        await deleteCustomerAddressAsync({ addressId });
        await refetchCustomerAddresses();
      } else {
        removeAddress(addressId);
        await refetchCustomerAddresses();
      }

      if (addressId === currentShippingAddressId) {
        const firstAvailableAddress = displayAddresses?.find((address) => address.id !== addressId);

        if (firstAvailableAddress) {
          // updateOrderData({ address: firstAvailableAddress });
          setCurrentShippingAddressId(firstAvailableAddress.id!, "MyAccountComponent");
          if (config.isExpressDeliveryEnabled && firstAvailableAddress?.latitude && firstAvailableAddress?.longitude) {
            await checkAndSetExpressMode({
              latitude: firstAvailableAddress.latitude,
              longitude: firstAvailableAddress.longitude,
            });
          }
        } else if (shippingZones) {
          setCurrentShippingAddressId("", "MyAccountComponent");
          if (config.isExpressDeliveryEnabled) {
            await checkAndSetExpressMode({
              latitude: "",
              longitude: "",
            });
          }
        }
      }
    } finally {
      setDeletingAddressId(null);
    }
  };

  return (
    <IonRadioGroup value={currentShippingAddressId}>
      <div className="flex flex-col gap-y-4">
        {displayAddresses?.map((address) => {
          const isDeleting = deletingAddressId === address.id;
          return (
            <div
              key={address.id}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                !isDeleting && handleChangeSelectAddress(address.id!);
              }}
              className={cx("address-item", "w-full flex items-center justify-between  cursor-pointer", {
                "opacity-50": isDeleting,
              })}
            >
              <div className="flex items-center gap-x-2.5 h-full">
                <div className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center">
                  {currentShippingAddressId === address.id && (
                    <div className="flex items-center justify-center w-full h-full bg-green-500 rounded-full">
                      <IonIcon size="small" icon={checkmark} className="text-white" />
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-start h-full">
                  <div className="text-sm text-gray-400">Адрес</div>
                  <div className={cx("address-text", "text-sm text-gray-900")}>
                    {getFullAddressForAddressType(address)}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-x-2">
                <button
                  disabled={isDeleting}
                  className="w-8 h-8 flex items-center justify-center cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleDeleteAddress(address.id!);
                  }}
                >
                  <IonIcon icon={trash} className="text-red-500" />
                </button>
                <button
                  disabled={isDeleting}
                  className="w-8 h-8 flex items-center justify-center cursor-pointer"
                  onClick={() => openEditMode(address)}
                >
                  <IonIcon icon={ellipsisHorizontal} className="text-gray-500" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </IonRadioGroup>
  );
};

export default AddressRadioGroup;
